// @flow

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {Grid, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';

import {usePage} from '../../page/utils';
import CardRedeemDescription from './CardRedeemDescription/CardRedeemDescription';
import AddCardModal from '../../checkout/components/Modals/AddCardModal';
import {useStoreCountry} from '../../country/utils';
import {useLocation} from 'react-router-dom';

import RedeemPoints from './RedeemPoints/RedeemPoints';
import {colors} from '../../../assets/styles/colors';
import {AVAILABLE_REDEEMS} from '../api';
import LoadingSpinner from '../../../elements/LoadingSpinner/LoadingSpinner';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import PlanChangeSuccessModal from '../../checkout/components/Modals/PlanChangeSuccessModal';
import RedeemedSubscriptionModal from './Modals/RedeemedSubscriptionModal';
import AddCardRedeemModal from './Modals/AddCardRedeem';
import {useTypedSelector} from '../../../store';

import type {CheckoutAction} from '../../checkout/duck';
import type {SubscribeRequest} from '../../checkout/api';
import type {RedeemPointsRequest, StoreBenefit} from '../api';
import type {
  CardProfile,
  PaymentProvider,
  Subscription,
  SubscriptionSetting,
} from '../../../entities';

type Props = {
  subscribeError?: string,
  points?: number,
  referralError?: string,
  isRedeeming?: boolean,
  redeemError?: {[key: string]: any},
  redeemSuccess?: boolean,
  cardProfiles?: CardProfile[],
  paymentProviders?: PaymentProvider[],
  redeemStoreBenefit: RedeemPointsRequest => void,
  subscription?: Subscription,
  dueInformation?: Subscription,
  isFetchingCardProfiles?: boolean,
  fetchCards: () => void,
  fetchPaymentProviders: (payload: CheckoutAction) => void,
  performSubscribe: ({subscribeRequest: SubscribeRequest}) => void,
  saveSubscriptionSetting: (payload: CheckoutAction) => void,
  subscribeSuccess?: boolean,
  isFetchingStore?: boolean,
  saveSubscriptionSettingSuccess?: boolean,
  subscriptionSetting?: SubscriptionSetting,
  isFetchingSubscriptionSetting?: boolean,
  isSubscribing?: boolean,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '5rem',
  },
  whiteCard: {
    padding: theme.spacing(2),
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '8px',
  },
  title: {
    color: '#3A3A49',
    fontWeight: 600,
    fontSize: '16px',
    '& span': {
      margin: '0 0.25rem',
      fontWeight: 400,
      fontSize: '0.875rem',
      color: colors.grey600,
    },
  },
  containerAllRedeems: {
    margin: theme.spacing(2, 0),
  },
}));

export default usePage<Props>(Redeem, 'Referral');

const LAST_REWARD_KEY = 'LAST_REWARD';
function Redeem(props: Props) {
  const {
    subscribeError,
    points,
    referralError,
    cardProfiles,
    isRedeeming,
    redeemError,
    redeemSuccess,
    fetchCards,
    paymentProviders,
    fetchPaymentProviders,
    performSubscribe,
    saveSubscriptionSetting,
    redeemStoreBenefit,
    subscription,
    subscribeSuccess,
    isSubscribing,
    isFetchingStore,
    saveSubscriptionSettingSuccess,
    subscriptionSetting,
    isFetchingSubscriptionSetting,
    isFetchingCardProfiles,
  } = props;

  const classes = useStyles();
  const location = useLocation();
  const country = useStoreCountry();
  const queryObject = queryString.parse(location.search);

  const creationRequest = useTypedSelector(
    state => state.referral.creationRequest
  );
  const isFetchingCreationRequest = useTypedSelector(
    state => state.referral.isFetchingCreationRequest
  );

  const [cardError, setCardError] = useState<string>('');
  const [benefitSelected, setBenefitSelected] = useState<StoreBenefit | string>(
    ''
  );
  const [openChangePlanModal, setOpenChangePlanModal] = useState<boolean>(
    false
  );

  const [openAddCardModal, setOpenAddCardModal] = useState<boolean>(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState<boolean>(false);
  const [isRSModalOpen, setIsRSModalOpen] = useState<boolean>(false);
  const [alreadyRedeemed, setAlreadyRedeemed] = useState<boolean>(false);

  const paymentProvider = useMemo(() => {
    if (paymentProviders && paymentProviders.length) {
      return paymentProviders.find(pm => pm.currency.code === 'USD');
    }
  }, [paymentProviders]);

  const hasAtLeastOneCard =
    cardProfiles &&
    !!cardProfiles.find(cp => cp.current && cp.state === 'ACTIVE');

  const handleSubscriptionRedeem = useCallback(
    (benefit: StoreBenefit) => {
      //User doesn't have a card
      if (!hasAtLeastOneCard) {
        setLocalStorageLastReward(benefit);
        setIsCardModalOpen(true);
        return;
      }
      redeemStoreBenefit({benefit});
    },
    [hasAtLeastOneCard, redeemStoreBenefit]
  );

  const setLocalStorageLastReward = (benefit: StoreBenefit) => {
    localStorage.setItem(LAST_REWARD_KEY, benefit);
  };

  const handleRedeem = useCallback(
    (benefit: StoreBenefit) => {
      //Provisionally for not allowed new redeem.
      if (creationRequest) return setAlreadyRedeemed(true);

      localStorage.removeItem(LAST_REWARD_KEY);
      setBenefitSelected(benefit);
      switch (benefit) {
        case 'MONTHLY_PRO_PLUS_SUBSCRIPTION':
          handleSubscriptionRedeem(benefit);
          break;
        default:
          console.log('Not implemented yet');
          break;
      }
    },
    [creationRequest, handleSubscriptionRedeem]
  );

  const redeemCustomError = useMemo(() => {
    if (redeemError) {
      if (redeemError.error) {
        const {error, message} = redeemError.error;
        if (error === 'InsufficientStorePointsError')
          return 'No tienes los puntos suficientes para canjear este beneficio';
        return message;
      } else return 'No se pudo canjear el beneficio.';
    }
  }, [redeemError]);

  const handleAddCard = () => {
    setIsCardModalOpen(false);
    setOpenAddCardModal(true);
  };

  useEffect(() => {
    if (!cardProfiles) fetchCards();
  }, [cardProfiles, fetchCards]);

  useEffect(() => {
    if (country && !paymentProviders) {
      fetchPaymentProviders({providersQuery: {country: country.code}});
    }
  }, [country, fetchPaymentProviders, paymentProviders]);

  useEffect(() => {
    if (
      redeemSuccess &&
      creationRequest &&
      !isFetchingCreationRequest &&
      !subscribeSuccess &&
      !saveSubscriptionSettingSuccess &&
      ['MONTHLY_PRO_PLUS_SUBSCRIPTION'].includes(benefitSelected)
    ) {
      const shouldSubscribe =
        !subscription || (subscription && subscription.state === 'PENDING');
      // (!subscription && dueInformation) ||

      // (!subscription && !dueInformation);
      if (shouldSubscribe) {
        // const currentCard =
        //   cardProfiles &&
        //   cardProfiles.find(cp => cp.current && cp.state === 'ACTIVE');

        const subscribeRequest: {[key: string]: any} = {
          storeTierPlanId: creationRequest.storeTierPlan.id,
          automaticCharge: true,
          currency: 'USD',
          // paymentData: {
          //   payWithCard: true,
          //   userCardProfileId: currentCard ? currentCard.id : undefined,
          // },
        };

        //If store doesn't have a subscription, subscribe
        // otherwise update subscription setting.

        const shouldMakeSuscribeAction =
          !subscription || (subscription && subscription.state === 'PENDING');

        if (shouldMakeSuscribeAction) performSubscribe({subscribeRequest});
        else {
          saveSubscriptionSetting({
            accountConfigRequest: {
              automaticCharge: true,
              storeTierPlanId: creationRequest.storeTierPlan.id,
              currency: 'USD',
            },
          });
        }
      } else setIsRSModalOpen(true);
    }
  }, [
    benefitSelected,
    cardProfiles,
    creationRequest,
    isFetchingCreationRequest,
    performSubscribe,
    redeemSuccess,
    saveSubscriptionSetting,
    saveSubscriptionSettingSuccess,
    subscribeSuccess,
    subscription,
  ]);

  useEffect(() => {
    if (
      (saveSubscriptionSettingSuccess &&
        subscription &&
        subscriptionSetting &&
        subscriptionSetting.storeTierPlan) ||
      (subscribeSuccess &&
        subscription &&
        subscriptionSetting &&
        !isFetchingSubscriptionSetting &&
        !isFetchingStore &&
        subscriptionSetting.storeTierPlan &&
        subscriptionSetting.storeTierPlan.storeTier.name !==
          subscription.storeTierPlan.storeTier.name)
    ) {
      setOpenChangePlanModal(true);
    } else setOpenChangePlanModal(false);
  }, [
    isFetchingStore,
    isFetchingSubscriptionSetting,
    saveSubscriptionSettingSuccess,
    subscribeSuccess,
    subscription,
    subscriptionSetting,
  ]);

  const addCard =
    queryObject.card_profile &&
    queryObject.setup_intent &&
    queryObject.setup_intent_client_secret &&
    queryObject.redirect_status === 'succeeded';

  useEffect(() => {
    if (addCard) {
      if (!isFetchingCardProfiles && cardProfiles) {
        if (hasAtLeastOneCard) {
          //User has added a new valid card
          const benefit = localStorage.getItem(LAST_REWARD_KEY);
          if (!!AVAILABLE_REDEEMS.find(bf => bf.label === benefit)) {
            // $FlowFixMe
            handleRedeem(benefit);
          }
        } else setCardError('Hubo un error al agregar la tarjeta');
      }
    } else localStorage.removeItem(LAST_REWARD_KEY);
  }, [
    addCard,
    cardProfiles,
    handleRedeem,
    hasAtLeastOneCard,
    isFetchingCardProfiles,
  ]);

  return (
    <>
      {(isRedeeming || isSubscribing) && <LoadingSpinner />}
      {redeemCustomError && (
        <SnackBar mode="error" message={redeemCustomError} />
      )}
      {!!subscribeError && (
        <SnackBar
          mode="error"
          message="Contacta a soporte para revisar tu caso."
        />
      )}
      {!!cardError && <SnackBar mode="error" message={cardError} />}
      {/* {redeemSuccess && (
        <SnackBar mode="success" message="Beneficio obtenido" />
      )} */}
      {alreadyRedeemed && (
        <SnackBar
          mode="error"
          message="Ya cuentas con una suscripción en proceso de canje."
          handleClose={() => setAlreadyRedeemed(false)}
        />
      )}
      {!referralError ? (
        <div className={classes.root}>
          <Grid container>
            <Grid item xs={12} md={6}>
              <RedeemPoints points={points} />
            </Grid>
          </Grid>
          <div className={classes.whiteCard}>
            <Typography className={classes.title}>Canjea tus puntos</Typography>
            <div className={classes.containerAllRedeems}>
              <Grid container spacing={2}>
                {AVAILABLE_REDEEMS.map(rd => (
                  <Grid key={rd.name} item xs={6} sm={3} md={2}>
                    <CardRedeemDescription
                      name={rd.name}
                      points={rd.points}
                      label={rd.label}
                      handleRedeem={handleRedeem}
                      disabled={Number(rd.points) > Number(points)}
                    />
                  </Grid>
                ))}
              </Grid>
            </div>
          </div>
        </div>
      ) : null}
      <AddCardModal
        open={openAddCardModal}
        handleClose={() => setOpenAddCardModal(false)}
        paymentProvider={paymentProvider}
      />
      <AddCardRedeemModal
        open={isCardModalOpen}
        handleClose={() => setIsCardModalOpen(false)}
        onClick={handleAddCard}
      />
      <RedeemedSubscriptionModal
        open={isRSModalOpen}
        handleClose={() => setIsRSModalOpen(false)}
      />
      {openChangePlanModal && <PlanChangeSuccessModal />}
    </>
  );
}

Redeem.propTypes = {
  subscribeError: PropTypes.string,
  subscription: PropTypes.object,
  dueInformation: PropTypes.object,
  points: PropTypes.number,
  referralError: PropTypes.string,
  isRedeeming: PropTypes.bool,
  redeemError: PropTypes.object,
  redeemSuccess: PropTypes.bool,
  cardProfiles: PropTypes.array,
  paymentProviders: PropTypes.array,
  fetchCards: PropTypes.func.isRequired,
  isFetchingCardProfiles: PropTypes.bool,
  fetchPaymentProviders: PropTypes.func.isRequired,
  redeemStoreBenefit: PropTypes.func.isRequired,
  performSubscribe: PropTypes.func.isRequired,
  saveSubscriptionSetting: PropTypes.func.isRequired,
  subscribeSuccess: PropTypes.bool,
  isFetchingStore: PropTypes.bool,
  saveSubscriptionSettingSuccess: PropTypes.bool,
  subscriptionSetting: PropTypes.object,
  isFetchingSubscriptionSetting: PropTypes.bool,
  isSubscribing: PropTypes.bool,
};
